import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Api from '../service/http';
import Loading from '../components/Loading';
import SubscribeModal from '../components/SubscribeModal';
import SubscribePromoInlineCard from '../components/SubscribePromoInlineCard';
import { hasActiveAccess } from '../utils/subscription';

const CoursePage = () => {
  const { theme } = useSelector((state) => state.theme);
  const { user: contextUser } = useOutletContext() || {};
  const user = contextUser || JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const subscribed = hasActiveAccess(user);

  const getCourses = async () => {
    setIsLoading(true);
    try {
      const response = await Api.get('/app/courses');
      setCourses([...response.data]);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCourses();
  }, []);

  useEffect(() => {
    if (location.state?.showSubscribeModal) {
      setShowSubscribeModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state?.showSubscribeModal, navigate]);

  const openCourse = (courseId) => {
    if (!subscribed) {
      setShowSubscribeModal(true);
      return;
    }
    navigate(`/course/${courseId}`);
  };

  return (
    <>
      <div className="flex h-full flex-1 flex-col overflow-hidden p-4 sm:p-6">
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-xl ${
            theme === 'dark'
              ? 'border-slate-800 bg-slate-900/40'
              : 'border-slate-200 bg-white'
          }`}
        >
          {!subscribed ? (
            <div
              className={`shrink-0 border-b p-4 ${
                theme === 'dark' ? 'border-slate-700' : 'border-slate-200'
              }`}
            >
              <SubscribePromoInlineCard
                badgeText="MEMBERS ONLY"
                badgeIcon="lock"
                title="Unlock courses"
                subtitle="Videos, documents & lessons — subscribe for full access"
              />
            </div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            {isLoading ? (
              <div className="flex min-h-[12rem] flex-1 items-center justify-center">
                <Loading message="Loading Courses..." />
              </div>
            ) : courses.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {courses.map((course) => (
                  <div
                    key={course.id}
                    role="button"
                    tabIndex={0}
                    className={`relative cursor-pointer rounded-lg border p-4 shadow-sm transition hover:shadow-md ${
                      theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-300'
                    }`}
                    onClick={() => openCourse(course.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openCourse(course.id);
                      }
                    }}
                  >
                    <img
                      src={course.image}
                      alt={course.name}
                      className="h-48 w-full rounded-md object-cover"
                    />
                    <h3 className={`mt-4 text-lg font-semibold ${theme === 'dark' ? 'text-white' : ''}`}>
                      {course.name}
                    </h3>
                    <p
                      className={`mt-2 line-clamp-4 text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      {course.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`flex flex-col items-center justify-center py-16 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                }`}
              >
                <p className="text-base font-medium">No Courses Available</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <SubscribeModal
        open={showSubscribeModal}
        onClose={() => setShowSubscribeModal(false)}
        title="Unlock courses"
        description="Subscribe to access videos, documents, and the full course library."
      />
    </>
  );
};

export default CoursePage;
